import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { Trash2, ShoppingCart, Loader2, ArrowRight, ArrowLeft, Gift, Megaphone, FileText } from "lucide-react";
import { ClientCombobox } from "../components/ClientCombobox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import api from "../lib/api";

export default function CartPage() {
  const { cart, removeFromCart, updateQuantity, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get client passed back from checkout
  const passedClientId = location.state?.selectedClientId;
  
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(passedClientId || "");
  const [clientData, setClientData] = useState(null);
  const [activePromos, setActivePromos] = useState([]);
  const [orderType, setOrderType] = useState("order"); // "order" or "quote"

  useEffect(() => {
    if (user?.role !== "client") {
        fetchClients();
    } else if (user?.client_id) {
        fetchClientData(user.client_id);
    }
    fetchPromos();
  }, [user]);

  // Load client data if passed from checkout
  useEffect(() => {
    if (passedClientId && !selectedClientId) {
      setSelectedClientId(passedClientId);
    }
  }, [passedClientId]);

  useEffect(() => {
      if (selectedClientId) {
          fetchClientData(selectedClientId);
      }
  }, [selectedClientId]);

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchClientData = async (clientId) => {
      try {
          const response = await api.get(`/settings/pricing-info/${clientId}`);
          setClientData(response.data);
      } catch (error) {
          console.error("Error fetching pricing info", error);
      }
  };

  const fetchPromos = async () => {
      try {
          const res = await api.get("/promos");
          setActivePromos(res.data);
      } catch (e) {
          console.error("Failed to fetch promos");
      }
  };

  // Helper to simulate promo application for display
  const getProcessedCart = () => {
      // Clone items to avoid mutating state
      let displayItems = cart.map(i => ({ ...i }));
      let extraItems = [];

      const matchesTarget = (item, promo) => {
          if (promo.target_type === 'all') return true;
          if (promo.target_type === 'product' && promo.target_ids.includes(item.id)) return true;
          if (promo.target_type === 'category' && promo.target_ids.includes(item.category)) return true;
          return false;
      };

      activePromos.forEach(promo => {
          if (promo.type === 'nxm') {
              const buyQty = promo.buy_qty;
              const getQty = promo.get_qty;
              const qualifyingItems = displayItems.filter(i => matchesTarget(i, promo));
              const totalQty = qualifyingItems.reduce((sum, i) => sum + i.quantity, 0);
              
              if (totalQty >= buyQty) {
                  const numFree = Math.floor(totalQty / buyQty) * getQty;
                  if (numFree > 0) {
                      // Determine freebie product (use first qualifying item as reference)
                      const refItem = qualifyingItems[0];
                      if (refItem) {
                          const existing = extraItems.find(i => i.id === refItem.id && i.is_freebie && i.promo_ref === promo.title);
                          if (existing) {
                              existing.quantity += numFree;
                          } else {
                              extraItems.push({
                                  ...refItem,
                                  quantity: numFree,
                                  commercial_price: 0,
                                  is_freebie: true,
                                  promo_ref: promo.title
                              });
                          }
                      }
                  }
              }
          } else if (promo.type === 'gift') {
              const qualifyingItems = displayItems.filter(i => matchesTarget(i, promo));
              const totalQty = qualifyingItems.reduce((sum, i) => sum + i.quantity, 0);
              
              if (totalQty >= promo.buy_qty) {
                  const numGifts = Math.floor(totalQty / promo.buy_qty) * promo.get_qty;
                  if (numGifts > 0) {
                      extraItems.push({
                          id: `GIFT-${promo.id}`,
                          code: "OMAGGIO",
                          name: promo.gift_description || "Omaggio",
                          quantity: numGifts,
                          commercial_price: 0,
                          is_freebie: true,
                          promo_ref: promo.title,
                          image_url: "" 
                      });
                  }
              }
          }
      });

      return [...displayItems, ...extraItems];
  };

  const processedCart = getProcessedCart();

  // Calculate applied promos for the banner
  const appliedPromos = activePromos.filter(promo => {
      if (promo.target_type === 'all') return true;
      if (promo.target_type === 'product') {
          return cart.some(item => promo.target_ids.includes(item.id));
      }
      if (promo.target_type === 'category') {
          return cart.some(item => promo.target_ids.includes(item.category));
      }
      return false;
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const calculateLineTotal = (item) => {
    const basePrice = item.commercial_price || 0;
    const qty = item.quantity || 0;
    const excise = item.excise_duty || 0;
    const stamp = item.stamp_duty || 0;
    const vatRate = item.vat_rate || 22.0;
    
    let discountedPrice = basePrice;
    
    if (clientData) {
        if (clientData.channel_discount) discountedPrice *= (1 - clientData.channel_discount / 100);
        if (clientData.discount_1) discountedPrice *= (1 - clientData.discount_1 / 100);
        if (clientData.discount_2) discountedPrice *= (1 - clientData.discount_2 / 100);
        if (clientData.discount_3) discountedPrice *= (1 - clientData.discount_3 / 100);
    }

    const taxableBase = (discountedPrice * qty) + ((excise + stamp) * qty);
    const vatAmount = taxableBase * (vatRate / 100);
    const total = taxableBase + vatAmount;

    return {
      net: discountedPrice * qty,
      taxes: (excise + stamp) * qty,
      vat: vatAmount,
      total: total,
      taxable: taxableBase,
      unit_discounted: discountedPrice
    };
  };

  const totals = processedCart.reduce((acc, item) => {
    // Only count taxes for freebies if needed (usually freebies are tax free or have specific rules)
    // Here we assume freebie has price 0 so net is 0. Taxes might apply?
    // Let's assume freebies are fully free for now (price 0, no tax).
    if (item.is_freebie) return acc;

    const line = calculateLineTotal(item);
    return {
      net: acc.net + line.net,
      taxes: acc.taxes + line.taxes,
      vat: acc.vat + line.vat,
      total: acc.total + line.total,
      taxable: acc.taxable + line.taxable
    };
  }, { net: 0, taxes: 0, vat: 0, total: 0, taxable: 0 });

  const handleProceedToCheckout = () => {
    let targetClientId = user?.role === "client" ? user.client_id : selectedClientId;

    if (!targetClientId) {
      toast.error(user?.role === "client" ? "Errore: account cliente non associato." : "Seleziona un cliente per procedere.");
      return;
    }
    
    // Pass order type and selected client to checkout
    navigate("/checkout", { state: { orderType, selectedClientId: targetClientId } });
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-muted-foreground space-y-4 animate-fade-in">
        <ShoppingCart className="w-16 h-16 opacity-20" />
        <h2 className="text-2xl font-bold text-foreground">Il carrello è vuoto</h2>
        <p>Aggiungi dei prodotti dal catalogo per procedere.</p>
        <Button onClick={() => navigate("/products")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna al Catalogo
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in pb-12">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <ShoppingCart className="w-8 h-8" />
          Carrello
        </h1>
        <Button variant="ghost" onClick={() => navigate("/products")}>
          Continua gli acquisti
        </Button>
      </div>

      {appliedPromos.length > 0 && (
          <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-4 rounded-lg flex flex-col gap-2">
              <h3 className="font-bold text-green-800 dark:text-green-400 flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Promozioni Attivate!
              </h3>
              <div className="space-y-1">
                  {appliedPromos.map(promo => (
                      <div key={promo.id} className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                          <Gift className="w-4 h-4" />
                          <span>{promo.title}: {promo.description}</span>
                          {promo.type === 'nxm' && <span className="text-xs bg-green-200 dark:bg-green-900 px-2 py-0.5 rounded">Omaggio al Checkout</span>}
                          {promo.type === 'gift' && <span className="text-xs bg-purple-200 dark:bg-purple-900 px-2 py-0.5 rounded">Gadget Incluso</span>}
                      </div>
                  ))}
              </div>
          </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Riepilogo Articoli ({cart.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prodotto</TableHead>
                    <TableHead className="w-[100px]">Quantità</TableHead>
                    <TableHead className="text-center w-[60px]">U.M.</TableHead>
                    <TableHead className="text-right">Listino</TableHead>
                    <TableHead className="text-right">Netto</TableHead>
                    <TableHead className="text-right">Imposte</TableHead>
                    <TableHead className="text-right w-[80px]">IVA %</TableHead>
                    <TableHead className="text-right font-bold">Totale</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {processedCart.map((item, idx) => {
                    const line = calculateLineTotal(item);
                    return (
                      <TableRow key={item.id + idx} className={item.is_freebie ? "bg-green-50 dark:bg-green-900/20" : ""}>
                        <TableCell>
                          <div className="flex items-center gap-4">
                            {item.image_url ? (
                              <img 
                                src={item.image_url} 
                                alt={item.name} 
                                className="w-12 h-12 rounded object-cover bg-muted"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded bg-muted flex items-center justify-center text-xs">
                                {item.is_freebie ? <Gift className="w-6 h-6 text-green-600"/> : "No img"}
                              </div>
                            )}
                            <div>
                              <div className="font-medium flex items-center gap-2">
                                {item.name}
                                {item.is_freebie && <span className="text-[10px] bg-green-200 text-green-800 px-1.5 py-0.5 rounded font-bold uppercase">Omaggio</span>}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono">{item.code}</div>
                              {item.promo_ref && <div className="text-[10px] text-green-600">{item.promo_ref}</div>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.is_freebie ? (
                             <div className="font-bold text-center">{item.quantity}</div>
                          ) : (
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                              className="w-20"
                            />
                          )}
                        </TableCell>
                        <TableCell className="text-center text-xs text-muted-foreground">
                          {item.unit_type === 'cartoni' ? 'Cart.' : 'Pz.'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground line-through text-xs">
                          {!item.is_freebie && formatCurrency(item.commercial_price * item.quantity)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {formatCurrency(line.net)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-muted-foreground">
                          {formatCurrency(line.taxes)}
                          {(item.excise_duty > 0 || item.stamp_duty > 0) && (
                            <div className="text-[10px] text-muted-foreground opacity-70">
                              (A+C)
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {item.vat_rate || 22}%
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-foreground">
                          {formatCurrency(line.total)}
                        </TableCell>
                        <TableCell>
                          {!item.is_freebie && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-destructive hover:text-destructive"
                              onClick={() => removeFromCart(item.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Note e Istruzioni</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Note per l'ordine</Label>
                <Textarea 
                  placeholder="Es. Richiesta consegna urgente, orari di scarico..." 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order Summary */}
        <div className="space-y-6">
          <Card className="sticky top-6 border-accent/20 shadow-md">
            <CardHeader className="bg-accent/5 pb-4">
              <CardTitle>Totale Ordine</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              
              {user?.role !== "client" && (
                  <div className="space-y-2 mb-6">
                      <Label className="text-blue-800 dark:text-blue-300">Cliente Destinatario *</Label>
                      <ClientCombobox 
                        clients={clients}
                        value={selectedClientId}
                        onChange={setSelectedClientId}
                      />
                  </div>
              )}

              {clientData && (
                  <div className="text-xs bg-green-50 dark:bg-green-950/30 p-2 rounded border border-green-100 text-green-800 dark:text-green-300 mb-4">
                      <span className="font-semibold block mb-1">Sconti Applicati:</span>
                      {clientData.channel_discount > 0 && <div>• Canale: {clientData.channel_discount}%</div>}
                      {clientData.discount_1 > 0 && <div>• Sconto 1: {clientData.discount_1}%</div>}
                      {clientData.discount_2 > 0 && <div>• Sconto 2: {clientData.discount_2}%</div>}
                      {clientData.discount_3 > 0 && <div>• Sconto 3: {clientData.discount_3}%</div>}
                  </div>
              )}

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Totale Netto Scontato</span>
                  <span>{formatCurrency(totals.net)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Totale Imposte (Accisa + Contr.)</span>
                  <span>{formatCurrency(totals.taxes)}</span>
                </div>
                <div className="flex justify-between font-medium pt-2 border-t">
                  <span>Totale Imponibile</span>
                  <span>{formatCurrency(totals.taxable)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Totale IVA (Stimata)</span>
                  <span>{formatCurrency(totals.vat)}</span>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex justify-between items-end">
                <span className="font-bold text-lg">Totale da Pagare</span>
                <span className="font-bold text-3xl text-accent">{formatCurrency(totals.total)}</span>
              </div>
              
              <p className="text-xs text-muted-foreground bg-muted p-3 rounded mt-2">
                * Il calcolo finale potrebbe subire lievi variazioni in base agli sconti cliente specifici applicati dal sistema centrale.
              </p>

              {/* Order Type Selection */}
              <div className="mt-4 p-4 border rounded-lg bg-muted/30">
                <Label className="text-sm font-semibold mb-3 block">Tipo di documento</Label>
                <RadioGroup value={orderType} onValueChange={setOrderType} className="space-y-2">
                  <div className="flex items-center space-x-3 p-2 rounded hover:bg-muted cursor-pointer">
                    <RadioGroupItem value="order" id="order" />
                    <Label htmlFor="order" className="cursor-pointer flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-accent" />
                      <div>
                        <span className="font-medium">Ordine</span>
                        <p className="text-xs text-muted-foreground">Conferma e invia l'ordine</p>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-2 rounded hover:bg-muted cursor-pointer">
                    <RadioGroupItem value="quote" id="quote" />
                    <Label htmlFor="quote" className="cursor-pointer flex items-center gap-2">
                      <FileText className="w-4 h-4 text-orange-500" />
                      <div>
                        <span className="font-medium">Preventivo</span>
                        <p className="text-xs text-muted-foreground">Richiedi un preventivo senza impegno</p>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <Button 
                className={`w-full h-12 text-lg font-bold mt-4 ${orderType === 'quote' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-accent hover:bg-accent/90'}`}
                onClick={handleProceedToCheckout}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Elaborazione...
                  </>
                ) : orderType === 'quote' ? (
                  <>
                    Richiedi Preventivo
                    <FileText className="ml-2 h-5 w-5" />
                  </>
                ) : (
                  <>
                    Procedi al Checkout
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
